import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { emailService } from '../services/emailService';

interface CompactSupportFormProps {
  onClose: () => void;
}

const CompactSupportForm: React.FC<CompactSupportFormProps> = ({ onClose }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Please upload only image files');
      return;
    }
    
    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setError('Each image must be less than 5MB');
      return;
    }
    
    const totalFiles = [...attachments, ...files];
    if (totalFiles.length > 3) {
      setError('Maximum 3 images allowed');
      return;
    }
    
    setAttachments(totalFiles);
    setError('');
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await emailService.sendSupportMessage(formData, attachments, () => {});
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setAttachments([]);
      
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          <span className="font-orbitron text-xs tracking-wider text-blue-400 uppercase">
            Support Center
          </span>
        </div>
        <h2 className="text-2xl font-black font-orbitron">
          Contact <span style={{ color: 'var(--color-primary)' }}>Support</span>
        </h2>
        <p className="text-gray-300 font-rajdhani text-sm mt-2">
          Need help? Send us a message and we'll respond within 24-48 hours.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="p-3 border rounded-xl text-green-400 font-rajdhani text-sm animate-fade-in" style={{
          background: 'rgba(20, 83, 45, 0.2)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          boxShadow: `
            0 4px 16px rgba(34, 197, 94, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
          backdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center gap-2">
            <span className="text-xl">✓</span>
            <span className="font-bold">Message sent successfully!</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 border rounded-xl text-red-400 font-rajdhani text-sm" style={{
          background: 'rgba(127, 29, 29, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          boxShadow: `
            0 4px 16px rgba(239, 68, 68, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
          backdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name & Email Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-rajdhani text-gray-300 uppercase tracking-wide mb-1">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              className="w-full border rounded-xl px-3 py-2 text-sm text-white font-rajdhani focus:outline-none transition-all disabled:opacity-50 font-sans"
              placeholder="Your name"
              required
              style={{
                background: 'rgba(30, 41, 59, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-rajdhani text-gray-300 uppercase tracking-wide mb-1">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              className="w-full border rounded-xl px-3 py-2 text-sm text-white font-rajdhani focus:outline-none transition-all disabled:opacity-50 font-sans"
              placeholder="your@email.com"
              required
              style={{
                background: 'rgba(30, 41, 59, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-xs font-rajdhani text-gray-300 uppercase tracking-wide mb-1">
            Subject <span className="text-red-400">*</span>
          </label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            disabled={loading}
            className="w-full border rounded-xl px-3 py-2 text-sm text-white font-rajdhani focus:outline-none transition-all disabled:opacity-50 font-sans"
            required
            style={{
              background: 'rgba(30, 41, 59, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)';
            }}
          >
            <option value="">Select a subject...</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Technical Issue">Technical Issue</option>
            <option value="Billing Question">Billing Question</option>
            <option value="Feature Request">Feature Request</option>
            <option value="Bug Report">Bug Report</option>
            <option value="Account Help">Account Help</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Message */}
        <div>
          <label className="block text-xs font-rajdhani text-gray-300 uppercase tracking-wide mb-1">
            Message <span className="text-red-400">*</span>
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            disabled={loading}
            rows={4}
            className="w-full border rounded-xl px-3 py-2 text-sm text-white font-rajdhani focus:outline-none transition-all resize-none disabled:opacity-50 font-sans"
            placeholder="Describe your issue or question..."
            required
            style={{
              background: 'rgba(30, 41, 59, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)';
            }}
          />
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-xs font-rajdhani text-gray-300 uppercase tracking-wide mb-1">
            Attachments (Optional)
          </label>
          <label className="block">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={loading || attachments.length >= 3}
              className="hidden"
            />
            <div className="w-full px-3 py-2 border rounded-xl cursor-pointer transition-all text-center text-xs" style={{
              background: 'rgba(30, 41, 59, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 0 rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)'
            }}>
              <span className="text-blue-400 font-rajdhani font-bold">
                {attachments.length >= 3 ? '✓ Max attachments (3)' : '📎 Add Screenshots'}
              </span>
            </div>
          </label>

          {/* Attached Files */}
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border rounded-xl px-2 py-1 text-xs"
                  style={{
                    background: 'rgba(30, 41, 59, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span>🖼️</span>
                    <span className="font-rajdhani text-white truncate">{file.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    disabled={loading}
                    className="ml-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full relative group px-4 py-3 border rounded-xl font-orbitron font-bold text-sm tracking-wider transition-all overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: `
              0 4px 16px rgba(59, 130, 246, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `,
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = `
              0 6px 24px rgba(59, 130, 246, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.3),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `;
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = `
              0 4px 16px rgba(59, 130, 246, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <span className="relative z-10 text-white">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>SENDING...</span>
              </span>
            ) : (
              '📨 SEND MESSAGE'
            )}
          </span>
        </button>
      </form>
    </div>
  );
};

const HelpBubble: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <>
      {/* Floating Help Bubble - Smaller on mobile */}
      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[9999]">
        <button
          onClick={() => setIsOpen(true)}
          onMouseEnter={(e) => {
            setShowTooltip(true);
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = `
              0 8px 32px rgba(59, 130, 246, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.3),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `;
          }}
          onMouseLeave={(e) => {
            setShowTooltip(false);
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = `
              0 6px 24px rgba(59, 130, 246, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `;
          }}
          className="relative group w-12 h-12 md:w-16 md:h-16 rounded-full transition-all duration-300"
          style={{
            background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: `
              0 6px 24px rgba(59, 130, 246, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `,
            backdropFilter: 'blur(10px)'
          }}
          aria-label="Get Help & Support"
        >
          {/* Icon - Responsive size */}
          <div className="relative z-10 flex items-center justify-center h-full">
            <svg 
              className="w-6 h-6 md:w-8 md:h-8 text-white" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
        </button>

        {/* Tooltip - Hidden on mobile to avoid clutter */}
        {showTooltip && !isOpen && (
          <div className="hidden md:block absolute bottom-full right-0 mb-2 px-4 py-2 border rounded-xl whitespace-nowrap animate-fade-in" style={{
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: `
              0 4px 16px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.2)
            `,
            backdropFilter: 'blur(10px)'
          }}>
            <div className="font-rajdhani text-sm text-white font-bold">
              Need Help?
            </div>
            <div className="font-rajdhani text-xs text-gray-300">
              Click to contact support
            </div>
          </div>
        )}
      </div>

      {/* Support Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 animate-fade-in">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Modal Content - 3D Glass Effect */}
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl animate-slide-in" style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4), rgba(51, 65, 85, 0.3))',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.4),
              0 0 0 1px rgba(255, 255, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 -1px 0 rgba(0, 0, 0, 0.2)
            `
          }}>
            {/* Glass inner glow layer */}
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent 50%)',
                backdropFilter: 'blur(20px)'
              }}
            />
            
            {/* Outer border highlight for 3D effect */}
            <div 
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(to top, rgba(255, 255, 255, 0.15) 0%, transparent 30%)',
              }}
            />

            {/* Close Button with 3D glass effect */}
            <button
              onClick={() => setIsOpen(false)}
              className="sticky top-2 left-full ml-2 z-50 w-10 h-10 flex items-center justify-center rounded-full transition-all hover:scale-110"
              style={{
                background: 'rgba(30, 41, 59, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: `
                  0 4px 16px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2)
                `,
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                e.currentTarget.style.boxShadow = `
                  0 4px 16px rgba(59, 130, 246, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.3)
                `;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.background = 'rgba(30, 41, 59, 0.6)';
                e.currentTarget.style.boxShadow = `
                  0 4px 16px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2)
                `;
              }}
              aria-label="Close"
            >
              <svg className="w-5 h-5" style={{ color: 'var(--color-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Compact Support Form - Responsive padding */}
            <div className="relative px-4 py-4 md:px-6 md:py-6 -mt-10 md:-mt-10" style={{ backdropFilter: 'blur(20px)' }}>
              <CompactSupportForm onClose={() => setIsOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpBubble;

